import { useEffect, useState } from 'react'
import {
  Page,
  Card,
  BlockStack,
  Text,
  Banner,
  SkeletonBodyText,
  Box,
  InlineStack,
  Badge,
} from '@shopify/polaris'
import { api, ApiError } from '../lib/api'

interface ShopInfo {
  shop: {
    id: string
    name: string
    myshopifyDomain: string
    primaryDomain: { url: string; host: string }
    currencyCode: string
    plan: { displayName: string }
    email: string
  }
  meta: {
    apiVersion: string
    fetchedAt: string
  }
}

export function HelloPage() {
  const [data, setData] = useState<ShopInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        setLoading(true)
        setError(null)
        const result = await api<ShopInfo>('/api/shop-info')
        if (!cancelled) setData(result)
      } catch (err) {
        if (cancelled) return
        if (err instanceof ApiError) {
          setError(`${err.message} (HTTP ${err.status})`)
        } else if (err instanceof Error) {
          setError(err.message)
        } else {
          setError('Unknown error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Page title="Headwall Tools — Cart Config" subtitle="v0.1 · smoke test">
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Connection check
            </Text>
            <Text as="p" tone="subdued">
              This page verifies the full auth chain: App Bridge issues a session
              token, the Cloudflare Pages backend verifies it, and an authenticated
              call is made to the Shopify Admin API. If you see your shop name
              below, the integration is wired up correctly.
            </Text>
          </BlockStack>
        </Card>

        {error && (
          <Banner tone="critical" title="Connection failed">
            <Text as="p">{error}</Text>
            <Box paddingBlockStart="200">
              <Text as="p" tone="subdued">
                Common causes: missing or wrong env vars in Cloudflare Pages,
                Shopify custom app URLs not pointing at this deployment, or
                expired Admin API access token. See SETUP.md.
              </Text>
            </Box>
          </Banner>
        )}

        {loading && !error && (
          <Card>
            <SkeletonBodyText lines={4} />
          </Card>
        )}

        {data && !error && (
          <Card>
            <BlockStack gap="300">
              <InlineStack gap="200" blockAlign="center">
                <Text as="h3" variant="headingSm">
                  {data.shop.name}
                </Text>
                <Badge tone="success">Connected</Badge>
              </InlineStack>
              <BlockStack gap="100">
                <Field label="Shop domain" value={data.shop.myshopifyDomain} />
                <Field label="Primary domain" value={data.shop.primaryDomain.url} />
                <Field label="Plan" value={data.shop.plan.displayName} />
                <Field label="Currency" value={data.shop.currencyCode} />
                <Field label="Owner email" value={data.shop.email} />
                <Field
                  label="Admin API version"
                  value={data.meta.apiVersion}
                />
                <Field
                  label="Fetched at"
                  value={new Date(data.meta.fetchedAt).toLocaleString()}
                />
              </BlockStack>
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </Page>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <InlineStack gap="200" align="space-between" blockAlign="center" wrap={false}>
      <Text as="span" tone="subdued">
        {label}
      </Text>
      <Text as="span" fontWeight="medium">
        {value}
      </Text>
    </InlineStack>
  )
}
